import React, { useState, useEffect } from 'react';
import { AdminLayout } from '../../components/admin/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Check, X } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const AdminTestimonials = () => {
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  useEffect(() => {
    fetchTestimonials();
  }, []);

  const fetchTestimonials = async () => {
    try {
      const response = await axios.get(`${API}/testimonials`);
      setTestimonials(response.data);
    } catch (error) {
      console.error('Error fetching testimonials:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (testimonialId, isApproved) => {
    try {
      await axios.patch(`${API}/testimonials/${testimonialId}/approve`, { isApproved });
      toast({
        title: "Success",
        description: `Testimonial ${isApproved ? 'approved' : 'rejected'}.`,
      });
      fetchTestimonials();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to update testimonial.",
        variant: "destructive"
      });
    }
  };

  if (loading) {
    return (
      <AdminLayout title="Manage Testimonials">
        <div className="text-center py-8">Loading testimonials...</div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="Manage Testimonials">
      <div className="space-y-4">
        {testimonials.length === 0 ? (
          <Card>
            <CardContent className="py-8 text-center text-gray-600">
              No testimonials yet.
            </CardContent>
          </Card>
        ) : (
          testimonials.map((testimonial) => (
            <Card key={testimonial.id}>
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div>
                    <CardTitle className="text-lg">{testimonial.name}</CardTitle>
                    <p className="text-sm text-gray-600">{testimonial.role}</p>
                  </div>
                  <div className="flex gap-2">
                    <Badge className={testimonial.isApproved ? "bg-green-600" : "bg-gray-600"}>
                      {testimonial.isApproved ? 'Approved' : 'Pending'}
                    </Badge>
                    <div className="flex gap-1">
                      {[...Array(testimonial.rating)].map((_, i) => (
                        <span key={i} className="text-yellow-500">★</span>
                      ))}
                    </div>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-gray-700 mb-4 italic">"{testimonial.content}"</p>
                <div className="flex gap-2">
                  {!testimonial.isApproved && (
                    <Button
                      size="sm"
                      onClick={() => handleApprove(testimonial.id, true)}
                      className="bg-green-600 hover:bg-green-700"
                    >
                      <Check size={16} className="mr-1" />
                      Approve
                    </Button>
                  )}
                  {testimonial.isApproved && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleApprove(testimonial.id, false)}
                      className="text-red-600"
                    >
                      <X size={16} className="mr-1" />
                      Unapprove
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>
    </AdminLayout>
  );
};
